/**
 * Formatting helpers. Everything user-facing is Spanish (es-ES); everything
 * "measured" is rendered with tabular numerals.
 */

const DATE_LONG = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})
const DATE_MED = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short' })
const TIME_SHORT = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' })
const RELATIVE = new Intl.RelativeTimeFormat('es-ES', { numeric: 'auto' })

export function toDate(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value)
}

/** "23 de agosto de 2026" */
export const formatDateLong = (v: string | number | Date) => DATE_LONG.format(toDate(v))

/** "23 AGO" — for a gutter. */
export const formatDateGutter = (v: string | number | Date) =>
  DATE_MED.format(toDate(v)).replace('.', '').toUpperCase()

/** "23 ago" — a short, friendly date. */
export const formatDateShort = (v: string | number | Date) =>
  DATE_MED.format(toDate(v)).replace('.', '')

/** "23.08.26" — for the plate caption strip. */
export function formatDateStamp(v: string | number | Date): string {
  const d = toDate(v)
  const p = (n: number) => String(n).padStart(2, '0')
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + String(d.getFullYear()).slice(2)
}

export const formatTime = (v: string | number | Date) => TIME_SHORT.format(toDate(v))

/** "hace 4 días", "ayer", "hace 2 meses" */
export function formatRelative(v: string | number | Date): string {
  const then = toDate(v).getTime()
  const diffSec = Math.round((then - Date.now()) / 1000)
  const abs = Math.abs(diffSec)
  if (abs < 60) return 'ahora mismo'
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['minute', 60],
    ['hour', 3600],
    ['day', 86400],
    ['week', 604800],
    ['month', 2629800],
    ['year', 31557600],
  ]
  let chosen: [Intl.RelativeTimeFormatUnit, number] = units[0]
  for (const u of units) if (abs >= u[1]) chosen = u
  return RELATIVE.format(Math.round(diffSec / chosen[1]), chosen[0])
}

/** "14 m", "1,2 km", "1.600 km" */
export function formatDistance(metres: number | null | undefined): string {
  if (metres == null || !Number.isFinite(metres)) return '—'
  if (metres < 1000) return Math.round(metres) + ' m'
  const km = metres / 1000
  if (km < 10) return km.toFixed(1).replace('.', ',') + ' km'
  return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(km) + ' km'
}

/** The catalogue number that makes a cat an individual: "No 84920". */
export const formatCatalogNo = (n: number | null | undefined) =>
  n == null ? 'Nº —' : 'Nº ' + n

/** Match scores are shown as whole percentages; never invent decimals. */
export const formatScore = (score: number) => Math.round(score * 100) + '%'

export const formatCount = (n: number) => new Intl.NumberFormat('es-ES').format(n)

/** "1,4 MB" */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  const units = ['kB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i++
  }
  return value.toFixed(value < 10 ? 1 : 0).replace('.', ',') + ' ' + units[i]
}

export function pluralise(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/**
 * The sticker caption: "Córdoba · hace 4 días". Place and time, in plain words;
 * the catalogue number only when there is nothing friendlier to say.
 */
export function plateCaption(parts: {
  catalogNo?: number | null
  place?: string | null
  date?: string | number | Date | null
}): string {
  const bits: string[] = []
  if (parts.place) bits.push(parts.place)
  if (parts.date) bits.push(formatRelative(parts.date))
  if (bits.length === 0 && parts.catalogNo != null) bits.push(formatCatalogNo(parts.catalogNo))
  return bits.join(' · ')
}

/** Initials for the avatar fallback. Never more than two glyphs. */
export function initials(name: string | null | undefined): string {
  if (!name) return '·'
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '·'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[1][0]).toUpperCase()
}

/**
 * A cat is never anonymous on screen: without a name it goes by its
 * reference — "GATO#10012" — built from the catalogue number.
 */
export function catRefName(name: string | null | undefined, catalogNo: number | null | undefined): string {
  if (name) return name
  if (catalogNo == null) return 'Sin nombre'
  return 'GATO#' + String(catalogNo).padStart(4, '0')
}

/**
 * How old a cat is, read from its birthday: "2 años y 1 mes", "7 meses",
 * "1 año", "12 días" — or "¡hoy es su día!" on the birthday itself. (§0063)
 */
export function catAgeText(birthdayIso: string, now: Date = new Date()): string {
  const birth = new Date(birthdayIso + (birthdayIso.length === 10 ? 'T00:00:00' : ''))
  if (Number.isNaN(birth.getTime()) || birth > now) return ''
  let years = now.getFullYear() - birth.getFullYear()
  let months = now.getMonth() - birth.getMonth()
  if (now.getDate() < birth.getDate()) months -= 1
  if (months < 0) {
    years -= 1
    months += 12
  }
  if (years === 0 && months === 0) {
    const days = Math.floor((now.getTime() - birth.getTime()) / 86400_000)
    if (days <= 0) return '¡hoy es su día!'
    return days === 1 ? '1 día' : days + ' días'
  }
  const y = years === 0 ? '' : years === 1 ? '1 año' : years + ' años'
  const m = months === 0 ? '' : months === 1 ? '1 mes' : months + ' meses'
  return y && m ? y + ' y ' + m : y || m
}
